<template>
    <div>
        <table class="vrm-table vrm-table--add-row">
            <thead>
                <tr>
                    <th>
                        {{
                            $t('ess.from', {
                                i18nFallback: 'From',
                            })
                        }}
                    </th>
                    <th>
                        {{
                            $t('ess.to', {
                                i18nFallback: 'To',
                            })
                        }}
                    </th>
                    <th v-html="valueTitle"></th>
                    <th />
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in localRows" :key="row.id">
                    <td :class="{ error: row.from.error }">
                        <span class="static-date">
                            {{ row.from.time }}
                        </span>
                    </td>
                    <td :class="{ error: row.to.error }">
                        <vrm-time-input
                            v-if="row.to.time !== TIME_END"
                            v-model="row.to.time"
                            unique-key-string="multiRowSchedule"
                            max-time="23:30"
                            min-time="00:30"
                            :interval="30"
                            @input="validateSchedule"
                        ></vrm-time-input>
                        <span v-if="row.to.time === TIME_END" class="vrm-table__time-label">
                            {{ '23:59' }}
                        </span>
                    </td>
                    <td :class="{ error: row[valueField].error }">
                        <div
                            class="vrm-indicator-input vrm-indicator-input--left"
                            :data-indicator="currency.sign"
                            :style="{ '--indicator-width': calculateIndicatorWidth(currency.sign) }"
                        >
                            <input
                                v-if="valueType === SCHEDULE_VALUE_TYPES.NUMBER"
                                :id="'value-' + row.id"
                                v-model="row[valueField].value"
                                :disabled="disableFirstValue"
                                class="sg-element"
                                type="text"
                                inputmode="decimal"
                                @keydown="onKeyDown"
                                @input="onInputEvent"
                            />
                            <vrm-toggle
                                v-if="valueType === SCHEDULE_VALUE_TYPES.TOGGLE"
                                :id="'value-' + row.id"
                                v-model="row[valueField].value"
                                :disabled="disableFirstValue"
                                :tooltip="disableFirstValue ? firstValueTooltip : ''"
                                class="sg-element"
                                @input="validateSchedule"
                            />
                        </div>
                    </td>
                    <td class="vrm-table__actions">
                        <span class="vrm-table__icon--link" @click="deleteRow(index)">
                            <span
                                v-if="row.from.time !== '00:00'"
                                class="sg-vrm-icon_055_cross-16px"
                            />
                        </span>
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr class="vrm-table__action" @click="addRow">
                    <td colspan="4">
                        <span class="vrm-table__icon sg-vrm-icon_058_plus-round-16px" />
                        <span
                            v-html="$t('ess.add_time', { i18nFallback: 'Add time range' })"
                        ></span>
                    </td>
                </tr>
                <tr v-if="errorMessage">
                    <td colspan="4" class="vrm-table__error">
                        {{ errorMessage }}
                    </td>
                </tr>
            </tfoot>
        </table>
        <slot></slot>
    </div>
</template>

<script>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { CURRENCIES } from '@/vue/store/modules/currencies';
import VrmTimeInput from '@/vue/components/shared/date-time-picker/VrmTimeInput.vue';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import { SCHEDULE_VALUE_TYPES } from '../constants';

@Component({
    components: {
        VrmTimeInput,
        VrmToggle,
    },
})
@AngularLocaleBundle('ess')
export default class MultiRowSchedule extends Vue {
    @Prop({ type: Array, required: false })
    rows;
    @Prop({ type: String, required: true })
    id;
    @Prop({ type: String, required: true })
    valueTitle;
    @Prop({ type: String, required: true })
    valueField;
    @Prop({ type: String, required: true })
    valueType;
    @Prop({ type: Array, required: false })
    additionalValidators;
    // The default first value if there is only one row.
    // It can't be changed by the user
    @Prop({ required: false })
    firstValue;
    @Prop({ type: String, required: false })
    firstValueTooltip;

    SCHEDULE_VALUE_TYPES = SCHEDULE_VALUE_TYPES;

    localRows = [];
    hasError = false;
    errorMessage = '';

    TIME_START = '00:00';
    TIME_END = '00:00';
    ERROR_OVERLAPPING = this.$t('ess.error_overlapping_schedule', {
        i18nFallback: 'You cannot enter overlapping time periods',
    });
    ERROR_INCOMPLETE = this.$t('ess.error_incomplete_schedule', {
        i18nFallback: 'The schedule needs to be complete',
    });
    ERROR_INVALID_FORMAT = this.$t('form.invalidTimeFormat', {
        i18nFallback: 'Invalid time format',
    });
    ERROR_INVALID_INTERVAL = this.$t('form.invalidTimeInterval', {
        i18nFallback: 'Intervals can only end on a full or half hour',
    });

    mounted() {
        this.validateSchedule();
    }

    @Watch('rows', { immediate: true })
    async updateRows() {
        this.localRows = this.validateRows(this.rows);
    }

    getRandomId() {
        return Math.floor(Math.random() * 99999);
    }

    validateRows(rows, emit = false) {
        // Unset all errors
        rows = rows.map(row => {
            // Round to the nearest 30 min interval
            if (/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(row.to.time)) {
                let minutes = row.to.time.split(':')[1];
                minutes = minutes < 30 ? ':00' : ':30';
                row.to.time = row.to.time.split(':')[0] + minutes;
            }
            return this.getRowData(row.id, row.from, row.to, row[this.valueField], false);
        });

        this.hasError = false;
        this.errorMessage = '';
        if (rows.length === 0) {
            return [
                this.getRowData(
                    this.getRandomId(),
                    this.TIME_START,
                    this.TIME_END,
                    this.firstValue,
                    true,
                ),
            ];
        }

        rows.forEach((row, index) => {
            let previousRow = index > 0 ? rows[index - 1] : null;
            // Check first row
            if (index === 0 && row.from.time !== this.TIME_START) row.from.error = true;
            // Check last row
            if (index === rows.length - 1 && row.to.time !== this.TIME_END) row.to.error = true;

            // Check continuous time of rows in between
            if (previousRow !== null) {
                row.from.time = previousRow.to.time;
                if (previousRow.to.time === '') {
                    rows[index - 1].to.error = true;
                    this.errorMessage = this.ERROR_INCOMPLETE;
                }
                if (row.to.time !== '00:00' && row.to.time <= row.from.time) {
                    row.to.error = true;
                    rows[index].to.error = true;
                    this.errorMessage = this.ERROR_OVERLAPPING;
                }
                // Test time to match regex "/^$|^([01][0-9]|2[0-3]):[0-5][0-9]$/" (allows "")
                if (
                    !(
                        /^$|^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(row.to.time) &&
                        /^$|^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(row.from.time)
                    )
                ) {
                    row.to.error = true;
                    row.from.error = true;
                    this.errorMessage = this.ERROR_INVALID_FORMAT;
                }
                if (
                    !(
                        /^$|^([01][0-9]|2[0-3]):(30|00)$/.test(row.to.time) &&
                        /^$|^([01][0-9]|2[0-3]):(30|00)$/.test(row.from.time)
                    )
                ) {
                    row.to.error = true;
                    row.from.error = true;
                    this.errorMessage = this.ERROR_INVALID_INTERVAL;
                }
                if (!row.to.time) {
                    row.to.error = true;
                    this.errorMessage = this.ERROR_INCOMPLETE;
                }
            }

            if (row[this.valueField].value === '') {
                row[this.valueField].error = true;
                this.errorMessage = this.ERROR_INCOMPLETE;
            }
        });

        for (const validator of this.additionalValidators) {
            validator.validateRows(rows);

            if (!validator.hasError) {
                continue;
            }

            this.errorMessage = this.$t(validator.getErrorMessage());

            validator.clearErrors();
        }

        this.hasError = rows.some(row => {
            return row.from.error || row.to.error || row[this.valueField].error;
        });
        this.hasError = this.hasError || !!this.errorMessage;

        if (emit && !this.hasError) {
            setTimeout(() => {
                this.$emit('valid-rows', rows);
            });
        }

        if (this.hasError) this.$emit('error', true);

        return rows;
    }
    formatTime(row) {
        let timeFrom = typeof row.from.time !== 'undefined' ? row.from.time : row.from;
        let timeTo = typeof row.to.time !== 'undefined' ? row.to.time : row.to;
        let value =
            typeof row[this.valueField].value !== 'undefined'
                ? row[this.valueField].value
                : row[this.valueField];

        row.from = {
            time: timeFrom.toString(),
            error: false,
        };
        row.to = {
            time: timeTo.toString(),
            error: false,
        };
        row[this.valueField] = {
            value,
            error: false,
        };
        return row;
    }
    addRow() {
        switch (this.localRows.length) {
            case 0:
                // Add new
                this.localRows.push(
                    this.getRowData(this.getRandomId(), this.TIME_START, this.TIME_END),
                );
                break;
            default:
                // Split up
                var lastIndex = this.localRows.length - 1;
                this.localRows[lastIndex].to = '';
                this.formatTime(this.localRows[lastIndex]);
                this.localRows.push(
                    this.getRowData(this.getRandomId(), '', this.TIME_END, this.defaultValue),
                );
                break;
        }
        this.validateRows(this.localRows, true);
    }
    getRowData(id, from, to, value = '') {
        return this.formatTime({
            id: id,
            from: from,
            to: to,
            [this.valueField]: value,
        });
    }
    deleteRow(index) {
        this.localRows.splice(index, 1);

        this.localRows[this.localRows.length - 1].to = {
            time: this.TIME_END,
        };

        if (this.localRows.length === 1 && this.firstValue) {
            const firstRow = this.localRows[0];
            firstRow[this.valueField].value = this.firstValue;
        }

        this.validateSchedule();
    }

    validateSchedule() {
        this.$nextTick(() => {
            this.localRows = this.validateRows(this.localRows, true);
        });
    }

    onKeyDown($event) {
        const value = $event.target.value.toString();
        if (
            (!/[0-9,.]/.test($event.key) &&
                ![
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Backspace',
                    'Tab',
                    'Enter',
                    '-',
                ].includes($event.key)) ||
            (($event.key === '.' || $event.key === ',') && value.includes('.')) || // only allow one decimal point
            ($event.key === '-' && ($event.target.selectionStart !== 0 || value.includes('-'))) // only allow one minus sign at the beginning
        ) {
            $event.preventDefault();
        }
    }

    onInputEvent() {
        // Replace , with . in number fields
        this.localRows.forEach(row => {
            if (
                this.valueType !== SCHEDULE_VALUE_TYPES.NUMBER ||
                typeof row[this.valueField].value !== 'string'
            ) {
                return;
            }

            row[this.valueField].value = row[this.valueField].value.replace(/,/g, '.');
        });

        this.validateSchedule();
    }

    get currency() {
        return this.$store.getters[CURRENCIES.GET_ACTIVE];
    }

    get defaultValue() {
        if (this.valueType === SCHEDULE_VALUE_TYPES.TOGGLE) {
            return false;
        }

        return '';
    }

    get disableFirstValue() {
        return this.firstValue !== '' && this.localRows.length === 1;
    }

    calculateIndicatorWidth(sign) {
        return `${sign.length}rem`;
    }
}
</script>
